import React, { useState, useEffect } from "react";
import { H2, Text } from "../../../../../components/typography";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Button } from "@amzn/stencil-react-components/button";
import { UploadCard } from "../upload-card";
import { Input } from "@amzn/stencil-react-components/form";
import useArb from "../../../../../hooks/use-arb";
import { AttachmentSubType, AttachmentType, UploadDetails } from "../../models";
import { submitDocument, updateDocumentId } from "../../requests/requests";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment";
import {
  deleteCachedRequest,
  getCandidateData,
} from "../../../../../utility/candidate-data";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";

export function INEUploadLayout() {
  const bundle = useArb("HVHChecklistPantherResource");
  const uploadINEMessage = bundle.getMessage(
    "Checklist-MainPage-INE-Upload-ButtonText"
  );
  const imagesOfINE = bundle.getMessage("Checklist-MainPage-INE-Text");
  const inputINEExpirationLabel = bundle.getMessage(
    "Checklist-INE-Expiration-Date-Title"
  );
  const inputINENumberLabel = bundle.getMessage("Checklist-INE-Number-Title");
  const inputINENumberPlaceholder = bundle.getMessage(
    "Checklist-INE-Number-Title"
  );
  const inputINEExpirationPlaceholder = "DD-MM-YYYY";
  const submitMessage = bundle.getMessage(
    "Checklist-Document-Image-Submit-ButtonText"
  );
  const documentAcceptableFileMessage = bundle.getMessage(
    "Checklist-Document-Acceptable-File-Text"
  );

  const maxSizeMessage = bundle.getMessage(
    "Checklist-Document-Maximum-File-Size-Text"
  );
  const p0LimitFixMessage = bundle.getMessage("p0LimitFixMessage");
  const documentUploadDependencyServiceErrorMessage = bundle.getMessage(
    "Checklist-Document-General-Error-Message"
  );
  const documentIdUploadDependencyServiceErrorMessage = bundle.getMessage(
    "Checklist-Document-ID-General-Error-Message"
  );
  const [frontDocument, setFrontDocument] = useState<UploadDetails>();
  const [backDocument, setBackDocumnet] = useState<UploadDetails>();
  const [
    dependencyServiceErrorMessage,
    setDependencyServiceErrorMessage,
  ] = useState<string>();
  const [
    idUploadDependencyServiceErrorMessage,
    setIdUploadDependencyServiceErrorMessage,
  ] = useState<string>();
  const [blockSubmitButton, setBlockSubmitButton] = useState<boolean>(true);
  const [ineExpirationDate, setINEExpirationDate] = useState<string>("");
  const [ineNumber, setINENumber] = useState<string>("");
  const { requisitionId, applicationId } = useParams<{
    requisitionId: string;
    applicationId: string;
  }>();
  const [frontErrorMessage, setBackErrorMessage] = useState<string>();
  const [backErrorMessage, setFrontErrorMessage] = useState<string>();
  const history = useHistory();

  const backToMainPage = () => {
    const path = `/checklist/${requisitionId}/${applicationId}`;
    history.push(path);
  };

  useEffect(() => {
    // Back side is required for INE
    setBlockSubmitButton(
      !frontDocument?.imageBase64 ||
        !backDocument?.imageBase64 ||
        !validateINENumber.test(ineNumber) ||
        !validateINEExpirationDate(ineExpirationDate)
    );
  }, [frontDocument, backDocument, ineNumber, ineExpirationDate]);

  const updateDocument = async (
    image: string,
    fileName: string,
    contentType: string,
    side: string,
    applicationId?: string
  ) => {
    let candidateId = "";
    if (applicationId) {
      const candidateData = await getCandidateData(applicationId);
      candidateId = candidateData?.candidateId;
    }
    const documentData = {
      documentType: AttachmentType.INE,
      attachmentSubType: side,
      imageBase64: image,
      fileName: fileName,
      contentType: contentType,
      candidateId: candidateId,
    };
    if (side === AttachmentSubType.FRONT) {
      setFrontDocument(documentData);
    } else {
      setBackDocumnet(documentData);
    }
  };

  const validateINENumber = new RegExp("^[a-zA-Z0-9]{18}$");

  const validateINEExpirationDate = (input: string) => {
    //RegExp the input is correct format ("DD-MM-YYYY")
    // otherwise moment assumes that letters are 0s
    const validateFormateDate = new RegExp("^[0-9]{2}-[0-9]{2}-[0-9]{4}$");
    if (validateFormateDate.test(input)) {
      const ineDate = moment(input, "DD-MM-YYYY");
      if (ineDate.isValid() && ineDate.isSameOrAfter(moment(), "day")) {
        return true;
      }
    }
    return false;
  };

  const formatTimeStamp = (input: string) => {
    const splittedDate = input.split("-");
    const dateStr = `${splittedDate[2]}-${splittedDate[1]}-${splittedDate[0]}`;
    const isoStr = dateStr + "T00:00:00.000Z";
    return isoStr;
  };

  const clickEventSubmitDocument = async () => {
    let returnToMainPage = true;
    if (
      frontDocument &&
      backDocument &&
      validateINENumber.test(ineNumber) &&
      validateINEExpirationDate(ineExpirationDate)
    ) {
      const candidateId = frontDocument.candidateId;
      const expirationFormatted = formatTimeStamp(ineExpirationDate);
      const newFrontDoc = {
        ...frontDocument,
        expirationDate: expirationFormatted,
      };
      const newBackDoc = {
        ...backDocument,
        expirationDate: expirationFormatted,
      };
      const docs = [newFrontDoc];
      // Back is required for INE
      docs.push(newBackDoc);
      try {
        await updateDocumentId({
          applicationId,
          idData: {
            nationalIdType: AttachmentType.INE,
            idNumber: ineNumber,
            expirationDate: expirationFormatted,
          },
          candidateId,
        });
      } catch (err) {
        console.log(err);
        setIdUploadDependencyServiceErrorMessage(
          documentIdUploadDependencyServiceErrorMessage
        );
        returnToMainPage = false;
      }
      try {
        await submitDocument(applicationId, docs, setBlockSubmitButton); // replace bbid
      } catch (err) {
        setBlockSubmitButton(false);
        console.log(err);
        setDependencyServiceErrorMessage(
          documentUploadDependencyServiceErrorMessage
        );
        returnToMainPage = false;
      }
      deleteCachedRequest(applicationId); // delete stale data so that updated Id loads on home page
      if (returnToMainPage) backToMainPage();
    }
  };
  return (
    <Col
      padding={"10px"}
      display={"flex"}
      justifyContent={"center"}
      gridGap={6}
      maxWidth={"330px"}
    >
      <H2>{uploadINEMessage}</H2>
      <Text>
        {imagesOfINE} <strong style={{ color: "red" }}>*</strong>{" "}
      </Text>
      <Text>{documentAcceptableFileMessage}</Text>
      <Text>{maxSizeMessage}</Text>
      <Row padding={{ top: "10px", bottom: "10px" }} gridGap="S400">
        <UploadCard
          side={AttachmentSubType.FRONT}
          documentDetailsFn={updateDocument}
          setErrorMessage={setFrontErrorMessage}
          applicationId={applicationId}
          setDependencyErrorMessage={setDependencyServiceErrorMessage}
        />
        <UploadCard
          side={AttachmentSubType.BACK}
          documentDetailsFn={updateDocument}
          setErrorMessage={setBackErrorMessage}
          applicationId={applicationId}
          setDependencyErrorMessage={setDependencyServiceErrorMessage}
        />
      </Row>
      {frontErrorMessage ||
      backErrorMessage ||
      dependencyServiceErrorMessage ? (
        <MessageBanner type={MessageBannerType.Error}>
          {dependencyServiceErrorMessage ??
            frontErrorMessage ??
            backErrorMessage}
        </MessageBanner>
      ) : null}
      {idUploadDependencyServiceErrorMessage ? (
        <MessageBanner type={MessageBannerType.Error}>
          {idUploadDependencyServiceErrorMessage}
        </MessageBanner>
      ) : null}
      <Col padding={{ bottom: "10px" }}>
        <Text>
          {inputINENumberLabel} <strong style={{ color: "red" }}>*</strong>
        </Text>
        <Input
          placeholder={inputINENumberPlaceholder}
          maxLength={18}
          onChange={(e) => setINENumber(e.target.value)}
          type="text"
        ></Input>
        <Text>
          {inputINEExpirationLabel} <strong style={{ color: "red" }}>*</strong>
        </Text>
        <Input
          placeholder={inputINEExpirationPlaceholder}
          maxLength={10}
          onChange={(e) => setINEExpirationDate(e.target.value)}
          type="text"
        ></Input>
      </Col>
      <Row padding={{ top: "10px", bottom: "10px" }}>
        <MessageBanner type={MessageBannerType.Informational}>
          {p0LimitFixMessage}
        </MessageBanner>
      </Row>
      <Button
        onClick={clickEventSubmitDocument}
        aria-disabled={blockSubmitButton}
      >
        {submitMessage}
      </Button>
    </Col>
  );
}
